import { Badge, Card, Group, Stack, Text, Title, Divider, Button, useMantineColorScheme, useMantineTheme, Box } from "@mantine/core";
// Badge is still used in MatchRow for status indicators
import { Link } from "react-router-dom";

export type MatchType = "upcoming" | "past";

export interface MatchItem {
  id: string;
  opponent: string;
  scheduledAt: string; // ISO string for now
  result?: "W" | "L" | "D";
  score?: string; // e.g., "16-12"
  tournament?: string;
  type: MatchType;
}

/**
 * UserMatchFeed
 *
 * Displays a player's upcoming and past matches.
 * Accepts an optional route target for a "More Details" button.
 */
export interface MatchFeedProps {
  title?: string;
  items?: MatchItem[];
  /** Route to navigate for the expanded matches view */
  moreDetailsTo?: string;
  /** When true, the Card fills the parent height and the list area scrolls internally */
  fullHeight?: boolean;
  /** Custom message to display when there are no matches */
  emptyMessage?: string;
}

// Default to empty array - match data should come from API
const defaultItems: MatchItem[] = [];

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * UserMatchFeed component
 *
 * Renders upcoming and past matches grouped into sections.
 * Provides an optional "More Details" button that navigates to a detailed schedule route.
 *
 * TODO: Map `items` from API when available.
 */
export default function UserMatchFeed({ title = "Match Feed", items = defaultItems, moreDetailsTo, fullHeight, emptyMessage }: MatchFeedProps) {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const isDark = colorScheme === 'dark';

  const upcoming = items.filter((i) => i.type === "upcoming");
  const past = items.filter((i) => i.type === "past");

  // Status colors sourced from theme tokens for easy customization (see src/theme.ts -> other.status)
  const statusColor = (m: MatchItem) => {
    const status = (theme.other as any)?.status ?? {};
    if (m.type === 'upcoming') return theme.primaryColor; // brand color for upcoming
    if (m.result === 'W') return status.win ?? 'green';
    if (m.result === 'L') return status.lose ?? 'red';
    if (m.result === 'D') return status.draw ?? 'gray';
    return status.draw ?? 'gray';
  };

  return (
    <Card withBorder radius="md" p="md" style={{ height: fullHeight ? '100%' : undefined, width: '100%', display: 'flex' }}>
      <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
        <Group justify="space-between" align="center">
          {/* Title color is scheme-aware for stronger contrast. To change globally, see src/theme.ts or swap theme in appThemes. */}
          <Title order={4} c={isDark ? 'gray.0' : 'dark.7'}>{title}</Title>
        </Group>

        <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {/* Upcoming */}
          <SectionTitle text="Upcoming" />
          <Stack gap={8}
            styles={{
              root: { listStyle: "none" },
            }}
          >
            {upcoming.length === 0 && past.length === 0 && emptyMessage && (
              <Text c={isDark ? undefined : 'dimmed'} size="sm">{emptyMessage}</Text>
            )}
            {upcoming.length === 0 && !(emptyMessage && past.length === 0) && (
              <Text c={isDark ? undefined : 'dimmed'} size="sm">No upcoming matches yet.</Text>
            )}
            {upcoming.map((m) => (
              <MatchRow key={m.id} item={m} color={statusColor(m)} variant={isDark ? 'filled' : 'light'} />
            ))}
          </Stack>

          <Divider my="sm" />

          {/* Recent Results */}
          <SectionTitle text="Recent results" />
          <Stack gap={8}>
            {past.length === 0 && (
              <Text c={isDark ? undefined : 'dimmed'} size="sm">No past matches yet.</Text>
            )}
            {past.map((m) => (
              <MatchRow key={m.id} item={m} color={statusColor(m)} variant={isDark ? 'filled' : 'outline'} />
            ))}
          </Stack>
        </Box>

        {moreDetailsTo && (
          <Group justify="flex-end">
            <Button component={Link} to={moreDetailsTo} variant={isDark ? 'filled' : 'light'} size="xs">
              More Details
            </Button>
          </Group>
        )}
      </Stack>
    </Card>
  );
}

function SectionTitle({ text }: { text: string }) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  return (
    <Text fw={700} size="sm" tt="uppercase" c={isDark ? 'gray.5' : 'gray.6'}>
      {text}
    </Text>
  );
}

function MatchRow({ item, color, variant }: { item: MatchItem; color?: string; variant?: 'light' | 'filled' | 'outline' }) {
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const isUpcoming = item.type === "upcoming";
  const resolvedVariant = variant ?? (isUpcoming ? 'light' : 'outline');
  const resolvedColor = color ?? (isUpcoming ? 'blue' : item.result === 'W' ? 'green' : item.result === 'L' ? 'red' : 'gray');
  return (
    <Box component={Link} to={`/matches/${item.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <Group gap="sm" wrap="nowrap" align="center" style={{ cursor: 'pointer' }}>
        {/* Badge colors/variants can be tuned via src/theme.ts or by passing props here */}
        <Badge variant={resolvedVariant} color={resolvedColor}>
          {isUpcoming ? "UPCOMING" : item.result ?? ""}
        </Badge>
        <Text fw={600} size="sm" style={{ flex: 1 }}>
          vs {item.opponent}
        </Text>
        {item.score && (
          <Text size="sm" c={isDark ? undefined : 'dimmed'}>
            {item.score}
          </Text>
        )}
        <Text size="sm" c={isDark ? undefined : 'dimmed'}>
          {formatDate(item.scheduledAt)}
        </Text>
      </Group>
    </Box>
  );
}
